/**
 * V2 Field Operations — Pure functions for V2 StepNode editor sections
 *
 * V2 equivalents of fieldOperations.ts, adapted for V2 data structures:
 * - Uses `translations` instead of `l10n`
 * - Paths are relative to StepNode (e.g., 'content.header.skip.content.translations.en')
 * - Reuses getValueByPath/isFieldEnabled from V1 (they're path-agnostic)
 *
 * Sprint: schema-v2-migration, Sprint 3
 */

// Re-export path-agnostic functions from V1 — they work identically for V2
export { getValueByPath, setValueByPath, isFieldEnabled, getTypeDefault } from './fieldOperations';

import { getValueByPath } from './fieldOperations';

/**
 * Get V2 localized value for a specific locale
 *
 * V2 uses `translations` instead of V1's `l10n`.
 * Path format: `{basePath}.translations.{locale}`
 *
 * @param obj - Source StepNode object
 * @param basePath - Path to the object containing translations (e.g., 'content.copy.title')
 * @param locale - Locale code (e.g., 'en-US')
 * @returns Localized string value or empty string if not found
 *
 * @example
 * getV2TranslationValue(step, 'content.copy.title', 'en-US')
 * // reads step.content.copy.title.translations['en-US']
 */
export function getV2TranslationValue(obj: unknown, basePath: string, locale: string): string {
  const translationsPath = basePath.endsWith('.translations')
    ? basePath
    : `${basePath}.translations`;
  const translations = getValueByPath<Record<string, string>>(obj, translationsPath);

  if (!translations || typeof translations !== 'object') {
    return '';
  }

  return translations[locale] ?? '';
}

/**
 * Set nested value by dot-notation path (mutates the object in-place)
 *
 * Used by V2 dialog's handleContentChange to update StepNode state.
 * Note: This mutates in-place — caller should deep-clone first if immutability is needed.
 *
 * @param obj - Target object to mutate
 * @param path - Dot-notation path (e.g., 'content.header.skip.content.translations.en')
 * @param value - Value to set
 */
export function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  let current: Record<string, unknown> = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  current[keys[keys.length - 1]] = value;
}

// ── Per-viewport typography routing (v2.19.1, article documents only) ───────
// Typography style writes arrive as dot-notation paths ending in
// `.appearance.<field>` or `.appearance.insets.<edge>` (or starting with
// `appearance.` for section-inner paths). In profile mode those paths are
// redirected into the sparse `presentationOverrides.{profile}` object so base
// values stay intact. Shared by the dialog's handleContentChange (direct
// header/actionBar writes) and FlexibleSectionContentAdapter.proxyOnChange
// (per-section content writes).

export type TypographyOverrideProfile = 'base' | 'standard' | 'phone' | 'tablet' | 'laptop';

const TYPOGRAPHY_OVERRIDE_FIELD_RE = /^((?:.+\.)?appearance)\.(fontSize|lineHeight|fontWeight|fontFamily|align)$/;
const TYPOGRAPHY_OVERRIDE_INSETS_RE = /^((?:.+\.)?appearance)\.insets\.(top|bottom|start|end)$/;

/**
 * Routes a typography style path to its per-profile override path.
 * Returns the path unchanged for base profile or non-typography paths.
 */
export function routeTypographyOverridePath(path: string, profile: TypographyOverrideProfile): string {
  if (profile === 'base') return path;
  const field = path.match(TYPOGRAPHY_OVERRIDE_FIELD_RE);
  if (field) return `${field[1]}.presentationOverrides.${profile}.${field[2]}`;
  const inset = path.match(TYPOGRAPHY_OVERRIDE_INSETS_RE);
  if (inset) return `${inset[1]}.presentationOverrides.${profile}.insets.${inset[2]}`;
  return path;
}

// ── Screen-default section layout routing (v2.19.1, article documents only) ─
// The "Default section layout" gear-panel block writes dot-notation paths under
// `content.flexibleLayout.defaultSectionLayout.*`. In profile mode the eligible
// fields (same set as per-section SectionLayout overrides) are redirected into
// `defaultSectionLayout.presentationOverrides.{profile}.*` so base defaults stay
// intact. Container-level insets (`content.flexibleLayout.insets.*`) are NOT
// eligible and pass through unchanged.

const DEFAULT_LAYOUT_OVERRIDE_FIELD_RE =
  /^((?:.+\.)?defaultSectionLayout)\.(heightKind|heightValue|widthKind|widthValue|maxWidth|minWidth|sectionHorizontalAlignment)$/;
const DEFAULT_LAYOUT_OVERRIDE_INSETS_RE =
  /^((?:.+\.)?defaultSectionLayout)\.(padding|margin)\.(top|bottom|start|end)$/;

/**
 * Routes a defaultSectionLayout path to its per-profile override path.
 * Returns the path unchanged for base profile or non-eligible paths.
 */
export function routeDefaultLayoutOverridePath(
  path: string,
  profile: TypographyOverrideProfile,
): string {
  if (profile === 'base') return path;
  const field = path.match(DEFAULT_LAYOUT_OVERRIDE_FIELD_RE);
  if (field) return `${field[1]}.presentationOverrides.${profile}.${field[2]}`;
  const inset = path.match(DEFAULT_LAYOUT_OVERRIDE_INSETS_RE);
  if (inset) return `${inset[1]}.presentationOverrides.${profile}.${inset[2]}.${inset[3]}`;
  return path;
}

/**
 * Delete a nested property by dot-notation path (mutates the object in-place).
 * Missing intermediate objects are a no-op. Used when nullable fields are
 * cleared (null) so Firebase data stays free of null/undefined keys.
 */
export function deleteNestedValue(obj: Record<string, unknown>, path: string): void {
  const keys = path.split('.');
  let current: Record<string, unknown> = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key] || typeof current[key] !== 'object') return;
    current = current[key] as Record<string, unknown>;
  }

  delete current[keys[keys.length - 1]];
}

/**
 * After an override key is deleted, remove now-empty ancestor objects up to and
 * including the `presentationOverrides` map itself, so cleared profiles leave
 * no `{}` residue in the document. No-op for paths without a
 * presentationOverrides segment. Stops at the first non-empty ancestor.
 */
export function pruneEmptyPresentationOverrides(
  obj: Record<string, unknown>,
  routedPath: string,
): void {
  const keys = routedPath.split('.');
  const mapIdx = keys.lastIndexOf('presentationOverrides');
  if (mapIdx === -1) return;

  // Candidate containers, deepest first: the deleted key's parent (e.g.
  // `...presentationOverrides.phone.padding`) up to the map itself.
  for (let end = keys.length - 1; end >= mapIdx + 1; end--) {
    let parent: Record<string, unknown> | undefined = obj;
    for (let i = 0; i < end - 1; i++) {
      const next = parent?.[keys[i]];
      if (!next || typeof next !== 'object') return;
      parent = next as Record<string, unknown>;
    }
    const key = keys[end - 1];
    const container = parent?.[key];
    if (
      container &&
      typeof container === 'object' &&
      !Array.isArray(container) &&
      Object.keys(container as Record<string, unknown>).length === 0
    ) {
      delete parent![key];
    } else {
      return;
    }
  }
}

/**
 * Get V2 appearance field value
 *
 * V2 uses `appearance` objects for styling (not nested `styles`).
 * Convenience wrapper for common pattern.
 *
 * @param obj - Source StepNode object
 * @param basePath - Path to the element (e.g., 'content.copy.title')
 * @param field - Appearance field name (e.g., 'fontSize', 'color', 'align')
 * @returns Field value or undefined
 */
export function getV2AppearanceValue<T = unknown>(
  obj: unknown,
  basePath: string,
  field: string
): T | undefined {
  return getValueByPath<T>(obj, `${basePath}.appearance.${field}`);
}
